import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { isAdminUser } from '../utils/authUser';

interface Permission {
    id: number;
    name: string;
    resource: string;
    action: string;
    description: string;
}

interface PermissionCheck {
    hasPermission: (resource: string, action: string) => boolean;
    permissions: Permission[];
    loading: boolean;
}

export const usePermissions = (): PermissionCheck => {
    const { user, isAuthenticated } = useAuth();
    const [permissions, setPermissions] = useState<Permission[]>([]);
    const [loading, setLoading] = useState(true);

    // Extract roleId to a stable primitive value to avoid unnecessary re-renders
    const roleId = user?.roleId;

    useEffect(() => {
        const fetchPermissions = async () => {
            if (!isAuthenticated || !roleId) {
                console.log('🔍 Permission Check: User not authenticated or no roleId', { isAuthenticated, roleId });
                setPermissions([]);
                setLoading(false);
                return;
            }

            try {
                // Fetch role details with permissions
                console.log('🔍 Fetching permissions for roleId:', roleId);
                const response = await api.get(`/api/roles/${roleId}`);
                console.log('🔍 API Response:', response.data);
                // Note: API returns "Permissions" (capital P), not "permissions"
                const rolePermissions = response.data.Permissions || response.data.permissions || [];
                console.log('🔍 Permissions loaded:', rolePermissions.length, 'permissions');
                console.log('🔍 Available resources:', Array.from(new Set(rolePermissions.map((p: Permission) => p.resource))));
                console.log('🔍 Full permissions:', rolePermissions.map((p: Permission) => `${p.resource}.${p.action}`));
                setPermissions(rolePermissions);
            } catch (error) {
                console.error('❌ Error fetching permissions:', error);
                setPermissions([]);
            } finally {
                setLoading(false);
            }
        };

        fetchPermissions();
    }, [roleId, isAuthenticated]);

    const hasPermission = (resource: string, action: string): boolean => {
        // Keep admin detection consistent with navigation and auth payload handling.
        const isAdmin = isAdminUser(user);
        if (isAdmin) {
            console.log(`✅ Admin user - granted ${resource}.${action}`);
            return true;
        }

        // Check if user has the specific permission
        const hasIt = permissions.some(
            (p) => p.resource === resource && p.action === action
        );

        if (hasIt) {
            console.log(`✅ Permission granted: ${resource}.${action}`);
        } else {
            console.warn(`❌ Permission DENIED: ${resource}.${action}`);
            console.warn(`   Looking for: resource="${resource}" AND action="${action}"`);
            console.warn(`   Total permissions loaded:`, permissions.length);
            console.warn(`   Available resources:`, Array.from(new Set(permissions.map(p => p.resource))));
            console.warn(`   Permissions with action="${action}":`, permissions.filter(p => p.action === action).map(p => p.resource));
        }

        return hasIt;
    };

    return {
        hasPermission,
        permissions,
        loading
    };
};

